import { computed, inject, type Ref } from 'vue'
import { listItems, useBloxApi, useData } from '@bagelink/blox'
import { isPublicRecord } from '../utils/publication'

const STORE = 'the_torah'
// ── Cache for resolved authors (avoid re-fetching) ──
let authorsById: Ref<Record<string, any>> | null = null

function ensureAuthors(options: { includeBio?: boolean } = {}) {
	const { items } = useCollection<any>('authors', {
		limit: 1000,
		all: true,
		pick: ['id', '_id', 'slug', 'name', 'title', 'image', 'institution', 'hidden', ...(options.includeBio ? ['bio'] : [])],
	})
	authorsById = computed(() => {
		const index: Record<string, any> = {}
		for (const author of items.value) {
			if (author._id) index[author._id] = author
			if (author.id) index[author.id] = author
			if (author.slug) index[author.slug] = author
		}
		return index
	})
	return items
}

/** Resolve an authorId (webflow _id or bagelink id) into author object */
export function resolveAuthor(authorId: string | undefined | null) {
	if (!authorId) return null
	return authorsById?.value[authorId] ?? null
}

/** Build a compact author entry for block props */
export function resolveAuthorForBlock(authorId: string | undefined | null) {
	const a = resolveAuthor(authorId)
	if (!a) return null
	return {
		name: a.name || '',
		title: a.title || '',
		image: a.image || '',
		slug: a.slug || '',
		link: a.slug ? `/author/${a.slug}` : '/authors',
		bio: a.bio || '',
		institution: a.institution || '',
		academy: a.institution || '',
	}
}

// ── Generic list fetcher ──

interface ListOptions {
	limit?: number
	offset?: number
	q?: string
	search?: string
	/** Remove heavy fields before data is serialized into the SSG payload. */
	omit?: string[]
	/** Keep only these fields in the SSG payload. */
	pick?: string[]
	/** Load every result page. Use only for collections required in full client-side. */
	all?: boolean
	/**
	 * Client-only data: never prefetched into the SSG payload. Use for a fallback
	 * that only exists to fill gaps the prerendered snapshot already covers.
	 */
	clientOnly?: boolean
	/** Skip the request entirely when this returns false (checked at fetch time). */
	enabled?: () => boolean
}

export function useCollection<T = any>(collection: string, options: ListOptions = {}) {
	// Capture injection during setup, before useData invokes the async loader.
	const api = useBloxApi()
	const limit = options.limit ?? 100
	const offset = options.offset ?? 0
	const omitted = (options.omit ?? []).filter(field => field !== '_id')
	const picked = options.pick?.length ? [...new Set(['_id', ...options.pick])] : []
	const key = `collection:${STORE}/${collection}:${offset}:${limit}:${options.q ?? ''}:${options.search ?? ''}:${options.all ?? false}:client=${options.clientOnly ?? false}:omit=${omitted.join(',')}:pick=${picked.join(',')}`
	const load = async (): Promise<T[]> => {
		if (options.enabled && !options.enabled()) return []
		const loaded: T[] = []
		let currentOffset = offset
		while (true) {
			const response = await (api?.listItems ?? listItems)<T>(collection, {
				q: options.q ?? options.search,
				limit,
				offset: currentOffset,
				// Ask for only the picked fields: large blobs (`content`, `footnotes`)
				// dominate the payload otherwise, and this list never reads them.
				...(picked.length ? { select: picked } : {}),
			})
			const rawPage = Array.isArray(response) ? [...response] : [...(response.items ?? [])]
			const page = rawPage.filter(item => isPublicRecord(collection, item as Record<string, unknown>))
			loaded.push(...(picked.length
				? page.map(item => Object.fromEntries(
						picked
							.filter(field => field in (item as Record<string, unknown>))
							.map(field => [field, (item as Record<string, unknown>)[field]]),
					) as T)
				: omitted.length
				? page.map((item) => {
						const compact = { ...item } as Record<string, unknown>
						for (const field of omitted) delete compact[field]
						return compact as T
					})
				: page))
			currentOffset += rawPage.length
			if (!options.all || rawPage.length < limit) break
		}
		return loaded
	}
	const { data: rawItems, pending: loading, error, refresh } = useData<T[]>(key, load, {
		default: [],
		// Client-only collections are never prefetched, so the prerendered payload
		// stays lean; the fetcher still hydrates on a plain SPA visit.
		...(options.clientOnly ? { server: false } : {}),
	})
	const items = computed(() => rawItems.value.filter(item => isPublicRecord(collection, item as Record<string, unknown>)))
	const total = computed(() => items.value.length)
	return { items, loading, error, total, refetch: refresh }
}

// ── Single item by slug ──

export function useItem<T = any>(collection: string, slug: Ref<string> | string) {
	const api = useBloxApi()
	const contexts = inject<Ref<Record<string, Record<string, unknown> | null>> | null>('contexts', null)
	const boundItem = computed(() => {
		const row = contexts?.value?.$page
		return row && isPublicRecord(collection, row) ? row as T : null
	})
	const slugValue = () => typeof slug === 'string' ? slug : slug.value
	const { data: fetchedItem, pending, error, refresh } = useData<T | null>(
		() => `item:${STORE}/${collection}:${slugValue()}`,
		async () => {
			if (boundItem.value) return boundItem.value
			const response = await (api?.listItems ?? listItems)<T>(collection, {
				q: `slug eq "${slugValue()}"`,
				limit: 1,
			})
			const items = Array.isArray(response) ? response : response.items
			return items.find(item => isPublicRecord(collection, item as Record<string, unknown>)) ?? null
		},
		{ default: null },
	)
	const item = computed(() => {
		const row = boundItem.value ?? fetchedItem.value
		return row && isPublicRecord(collection, row as Record<string, unknown>) ? row : null
	})
	const loading = computed(() => !boundItem.value && pending.value)
	async function refetch(_slug?: string) {
		if (!boundItem.value) await refresh()
	}
	return { item, loading, error, refetch }
}

// ── Fetch items with q filter ──

export function useFiltered<T = any>(collection: string, query: string, options: ListOptions = {}) {
	return useCollection<T>(collection, { ...options, q: query })
}

// ── Helper: fetch + resolve authors in one shot ──

export function useArticlesWithAuthors(options: ListOptions = {}) {
	const { items: rawArticles, loading, error, total, refetch } = useCollection('articles', {
		...options,
		pick: options.pick ?? [
			'id', '_id', 'slug', 'title', 'image', 'publishDate',
			'authorId', 'secondAuthorId', 'thirdAuthorId', 'authorIds',
			'authors',
			'topicIds', 'relatedArticleIds', 'seriesId', 'seriesOrder',
			'symposiumId', 'blogTypeId', 'hits', 'featuredHomepage', 'titleTag',
		],
	})

	const articles = computed(() =>
		rawArticles.value.map((a: any) => {
			if (Array.isArray(a.authors) && a.authors.length) {
				return { ...a, link: `/article/${a.slug}` }
			}
			const authorIds = Array.isArray(a.authorIds)
				? a.authorIds
				: [a.authorId, a.secondAuthorId, a.thirdAuthorId].filter(Boolean)
			const authors = authorIds.map(resolveAuthorForBlock).filter(Boolean)
			return {
				...a,
				authors,
				link: `/article/${a.slug}`,
			}
		})
	)

	return { articles, loading, error, total, refetch }
}

export { ensureAuthors }
